import './ApiVolumeGraph.scss';

import { Area } from '@ant-design/plots';
import { Button } from 'antd';
import { createAsyncSelectors } from '@lib/selectors';
import { DataSecurityViewQueryParams } from '@security/DataSecurityView';
import { getDate } from '@sonar-software/sonar-ts-lib';
import { SonarCard } from '@ui/general';
import { useNavigate } from 'react-router';

import { COPY_SPECIFIC, DATA_SECURITY_DASHBOARD, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { coreActions, DataSecurityApplicationReference, FeatureFlag } from '@sonar-software/sonar-core';
import React, { memo, useEffect, useMemo } from 'react';
import {
	useAction,
	useApplicationReferences,
	useAsyncState,
	useDataSecurityAppColors,
	useFeatureFlag,
	usePrevious
} from '@hooks';

const getCallsText = (calls: number): string => `${calls?.toLocaleString()} ${DATA_SECURITY_DASHBOARD.CALLS}`;

const getAppDisplayName = (id: number, app = {} as DataSecurityApplicationReference) => {
	const { name, label, deletedOn } = app;
	const nameDisplay = id === 0 ? COPY_SPECIFIC.ALL_OTHERS : name ?? id;
	const labelDisplay = label ? ` (${label})` : '';
	const deletedDisplay = deletedOn ? ` - ${COPY_SPECIFIC.HIDDEN}` : '';

	return `${nameDisplay}${labelDisplay}${deletedDisplay}-${id}`;
};

const withoutId = (appName: string) => appName.replace(/-\w+$/u, '');

const config = {
	fields: [ 'total' ],
	height: 358,
	legend: {
		itemName: { formatter: withoutId },
		itemSpacing: 4
	},
	seriesField: 'appName',
	tooltip: {
		formatter: (item: { date: string; appName: string; total: number; }) => ({
			name: withoutId(item.appName),
			value: getCallsText(item.total)
		}),
		title: (title: string) => getDate(title).format('ll')
	},
	xAxis: {
		label: {
			formatter: (text:string) => getDate(text).format('MMM D'),
			offsetX: -25,
			rotate: -45
		},
		title: {
			autoRotate: false,
			text: COPY_SPECIFIC.DATE
		}
	},
	xField: 'date',
	yAxis: {
		label: {
			formatter: (text:string) => Number(text).toLocaleString()
		},
		title: {
			text: DATA_SECURITY_DASHBOARD.API_CALLS
		}
	},
	yField: 'total'
};

export const getDataSecurityApiVolumeSelector = createAsyncSelectors((state) => state.core.dataSecurity.apiVolume.get);

export interface ApiVolumeGraphProps{
	applicationId?: number;
	viewParams: DataSecurityViewQueryParams;
	showExploreButton?: boolean;
}

export const ApiVolumeGraph : React.FC<ApiVolumeGraphProps> = memo(({
	applicationId,
	viewParams,
	showExploreButton
}) => {
	const allowOverview = useFeatureFlag(FeatureFlag.DataSecurityDashboard);
	const getDataSecurityApiVolume = useAction(coreActions.requestGetCoreDataSecurityApiVolume);
	const { data, isRequested } = useAsyncState(getDataSecurityApiVolumeSelector);
	const previousDateRange = usePrevious(viewParams.dateRange);
	const previousAppGroup = usePrevious(viewParams.appGroup);
	const navigate = useNavigate();
	const apps = useApplicationReferences();
	const appIds = data?.applications.map(({ id }) => id);
	const appColors = useDataSecurityAppColors(appIds);

	const colorsArray = useMemo(() =>
		appIds?.map((id) => appColors[ id ] ?? 'gray'),
	[ appColors, appIds ]);

	const graphData = useMemo(
		() => data?.applications?.reduce((allData, app) => [
			...allData,
			...app.volumes?.map(
				(volume) => ({
					...volume,
					appName: getAppDisplayName(app.id, apps[ app.id ])
				})
			) ?? []
		], []),
		[ apps, data?.applications ]
	);

	useEffect(() => {
		if (
			viewParams.dateRange.end !== previousDateRange?.end ||
			viewParams.dateRange.start !== previousDateRange?.start ||
			(!applicationId && viewParams.appGroup !== previousAppGroup)
		) {
			getDataSecurityApiVolume({
				applicationId,
				end: viewParams?.dateRange.end,
				stakeholderGroupId: viewParams?.appGroup,
				start: viewParams?.dateRange.start
			});
		}
	}, [ applicationId, viewParams, getDataSecurityApiVolume, previousDateRange, previousAppGroup ]);

	return (
		<SonarCard
			className='api-volume-card'
			extra={ allowOverview && showExploreButton &&
				<Button
					id='explore-button'
					onClick={ () => navigate(FULL_ROUTES.DATA_SECURITY_API_VOLUME) }>
					{ COPY_SPECIFIC.EXPLORE }
				</Button> }
			loading={ isRequested }
			noSpacing
			title={ DATA_SECURITY_DASHBOARD.API_VOLUME }
			titleClassName='h2'
			useSpinnerLoading>
			<Area
				className='api-volume-graph'
				color={ colorsArray }
				data={ graphData ?? [] }
				{ ...config } />
		</SonarCard>
	);
});
